import { EffectRef, useEffectRef } from '@huse/effect-ref';
import { slugify } from '@portals/core/src/utilities/slugify';
import React, { createContext, PropsWithChildren, useContext, useRef } from 'react';

interface SparkasseAssignmentFormContextProps {
  useFormRef(key: string, description: string): EffectRef<HTMLElement>;
  identifier(key: string, description: string): string;
  findForm(key: string, description: string): HTMLElement;
  findNearestForm(key: string, description: string): HTMLElement;
  handleResolve(key: string, description: string): Promise<{ bankCode: string }>;
  resolveLink(key: string, description: string, bankCode?: string): void;
}

export const SparkasseAssignmentFormContext = createContext<SparkasseAssignmentFormContextProps>({
  useFormRef: () => undefined,
  identifier: () => undefined,
  findForm: () => undefined,
  findNearestForm: () => undefined,
  handleResolve: () => undefined,
  resolveLink: () => undefined,
});

const Provider = SparkasseAssignmentFormContext.Provider;

interface SparkasseAssignmentFormList {
  [key: string]: HTMLElement[];
}

interface SparkasseAssignmentLinkList {
  [key: string]: (value: { bankCode: string }) => void;
}

export const SparkasseAssignmentFormProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const formListRef = useRef<SparkasseAssignmentFormList>({});
  const linkListRef = useRef<SparkasseAssignmentLinkList>({});

  const identifier = (key: string, description: string): string => slugify(`${key}-${description}`);

  const findForm = (key: string, description: string) => formListRef.current[identifier(key, description)]?.at(0);

  const findNearestForm = (key: string, description: string) => {
    formListRef.current[identifier(key, description)].sort((formA, formB) => {
      const { top: topA, bottom: bottomA } = formA.getBoundingClientRect();
      const { top: topB, bottom: bottomB } = formB.getBoundingClientRect();
      const minFormA = Math.min(Math.abs(topA), Math.abs(bottomA));
      const minFormB = Math.min(Math.abs(topB), Math.abs(bottomB));
      return minFormA - minFormB;
    });
    return formListRef.current[identifier(key, description)]?.at(0);
  };

  const createFormRef = (key: string, description: string, node: HTMLElement) => {
    if (!Array.isArray(formListRef.current[identifier(key, description)])) {
      formListRef.current[identifier(key, description)] = [];
    }

    formListRef.current[identifier(key, description)].push(node);
  };

  const deleteFormRef = (key: string, description: string, node: HTMLElement) => {
    formListRef.current[identifier(key, description)] = formListRef.current[identifier(key, description)].filter(
      (currentNode) => currentNode !== node
    );
  };

  const useFormRef = (key: string, description: string) =>
    useEffectRef((node) => {
      createFormRef(key, description, node);
      return () => {
        deleteFormRef(key, description, node);
      };
    });

  const resolveLink = (key: string, description: string, bankCode?: string) => {
    return linkListRef.current[identifier(key, description)]({ bankCode });
  };

  const handleResolve = (key: string, description: string): Promise<{ bankCode: string }> => {
    return new Promise((resolve) => {
      linkListRef.current[identifier(key, description)] = resolve;
    });
  };

  return (
    <Provider value={{ useFormRef, identifier, findForm, findNearestForm, handleResolve, resolveLink }}>
      {children}
    </Provider>
  );
};

export const useSparkassenAssignmentForm = (): SparkasseAssignmentFormContextProps => {
  const form = useContext(SparkasseAssignmentFormContext);

  if (!form) {
    throw Error(
      'The `useSparkassenAssignmentForm` hook must be called from a descendent of the `SparkasseAssignmentFormProvider`.'
    );
  }

  return {
    ...form,
  };
};
